import '../se-heading/se-heading';
import '../se-button/se-button';
import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './se-profile-teaser.css.lit';
import { SlotController } from '../../controllers/slot-controller';

/**
 * The Profile teaser component.
 * @element profile-teaser
 */
@customElement('profile-teaser')
export class ProfileTeaser extends OutlineElement {
  slots = new SlotController(this, true);
  static styles = [componentStyles];

  /**
   * Profile teaser required attributes
   */
  @property({ type: String, attribute: 'profile-url' })
  profileURL: string;
  @property({ type: String, attribute: 'full-name' })
  fullName: string;
  @property({ type: String, attribute: 'main-title' })
  mainTitle: string;
  @property({ type: String, attribute: 'v-card' })
  vCard: string;
  @property({ type: String, attribute: 'primary-office-location' })
  primaryOfficeLocation: string;
  @property({ type: String, attribute: 'primary-office-url' })
  primaryOfficeURL: string;

  /**
   * Profile teaser optional attributes
   */
  @property({ type: String, attribute: 'email' })
  email: string | boolean = false;
  @property({ type: String, attribute: 'phone' })
  phone: string | boolean = false;
  @property({ type: String, attribute: 'secondary-office-location' })
  secondaryOfficeLocation: string | boolean = false;
  @property({ type: String, attribute: 'secondary-office-url' })
  secondaryOfficeURL: string | boolean = false;

  bgTemplate(): TemplateResult {
    return html`
      <div class="profile-picture">
        <slot name="profile-picture"></slot>
      </div>
    `;
  }

  cardTemplate(): TemplateResult {
    return html`
      <section class="profile-card">
        <se-heading level="h2" level-size="h4" class="full-name">
          <a href="${this.profileURL}"> ${this.fullName} </a>
        </se-heading>
        <p class="main-title">${this.mainTitle}</p>
        ${this.email
          ? html`
              <a class="email" href="mailto:${this.email}">${this.email}</a>
            `
          : ''}
        ${this.phone
          ? html` <a class="phone" href="tel:${this.phone}">${this.phone}</a> `
          : ''}
        ${(this.primaryOfficeLocation && this.primaryOfficeURL) ||
        (this.secondaryOfficeLocation && this.secondaryOfficeURL)
          ? html`<nav aria-label="Locations information" class="offices-info">
              ${this.primaryOfficeLocation && this.primaryOfficeURL
                ? html`<ul>
                    <li class="office-label">Primary Office</li>
                    <li class="office-link">
                      <a href="${this.primaryOfficeURL}">
                        ${this.primaryOfficeLocation}
                      </a>
                    </li>
                  </ul>`
                : ''}
              ${this.secondaryOfficeLocation && this.secondaryOfficeURL
                ? html`
                    <ul>
                      <li class="office-label">Secondary Office</li>
                      <li class="office-link">
                        <a href="${this.secondaryOfficeURL}">
                          ${this.secondaryOfficeLocation}
                        </a>
                      </li>
                    </ul>
                  `
                : ''}
            </nav>`
          : ''}

        <se-button
          class="download-vcard"
          button-url="${this.vCard}"
          size="medium"
          variant="primary-2"
          downloadable
        >
          <outline-icon
            slot="left"
            name="calendar"
            library="system"
            size="1em"
            color="white"
          ></outline-icon>
          Download vCard
        </se-button>
      </section>
    `;
  }

  render(): TemplateResult {
    return html`
      <section class="profile-teaser">
        ${this.bgTemplate()} ${this.cardTemplate()}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'profile-teaser': ProfileTeaser;
  }
}
